import isEqual from 'lodash.isequal';
import { useState, useEffect, useCallback } from 'react';
import { useSingleCollectionValue, useModifySharedCollection, useReadCollectionValue, useWaitForCollectionValue } from './use-shared-collection';
import { useSingletonInitialization } from './use-singleton-initialization';
import { useResolvedCollectionValue } from './shared-value-resolving';
import { useSharedValueStoreContext } from './context/shared-values-context';
/**
 * This hook defines an async initialization that will only be called once globally.
 * This allows a developer to define the init logic of a shared value inside it's hook
 * without initialization being triggered multiple times
 * @deprecated Use initializers instead
 * @param key The shared value key
 * @param init An async initialization function that returns the inital value
 */
export var useSharedValueInitialization = function (key, init) {
    var setValue = useModifySharedValue(key);
    var apply = function (value) { return setValue(value); };
    return useSingletonInitialization(key, init, apply);
};
export function useReadSharedValue(key) {
    if (isValueFunction(key)) {
        return useReadFunction(key);
    }
    else if (isCollectionKey(key)) {
        return useReadCollectionValue(key);
    }
    else {
        return useReadSingleSharedValue(key);
    }
}
export function useModifySharedValue(key, collectionValueKey) {
    if (!isCollectionKey(key)) {
        return useModifySingleSharedValue(key);
    }
    else {
        var collection_1 = useModifySharedCollection(key);
        return useCallback(function (value) { return collection_1.setValue(collectionValueKey, value); }, [
            collection_1,
            collectionValueKey
        ]);
    }
}
export function useWaitForSharedValue(key, criteria) {
    if (isValueFunction(key)) {
        return useWaitForFunction(key);
    }
    else if (!isCollectionKey(key)) {
        return useWaitForSingleSharedValue(key, criteria);
    }
    else {
        return useWaitForCollectionValue(key, criteria);
    }
}
function useWaitForFunction(func, criteria) {
    var context = useSharedValueStoreContext();
    return function waitForFunction() {
        return context.waitFor(func, criteria);
    };
}
function useWaitForSingleSharedValue(key, criteria) {
    var context = useSharedValueStoreContext();
    return function waitForValue() {
        return context.waitFor(key, criteria);
    };
}
export function useSharedValue(key, collectionValueKeyOrArgs) {
    if (isValueFunction(key)) {
        return useValueFunction(key, collectionValueKeyOrArgs);
    }
    else if (!isCollectionKey(key)) {
        return useSingleSharedValue(key);
    }
    else if (isResolvedCollectionKey(key)) {
        return useResolvedCollectionValue(key, collectionValueKeyOrArgs);
    }
    else {
        return useSingleCollectionValue(key, collectionValueKeyOrArgs);
    }
}
function useValueFunction(func, volatileArgs) {
    if (volatileArgs === void 0) { volatileArgs = []; }
    var context = useSharedValueStoreContext();
    var memoFunc = useCallback(func, volatileArgs);
    var _a = useState(function () { return context.get(memoFunc); }), value = _a[0], setValue = _a[1];
    useEffect(function () {
        var newValue = context.get(memoFunc);
        if (!isEqual(value, newValue))
            setValue(newValue);
    }, [context, memoFunc]);
    useLatestChanges(function () { return context.changes(memoFunc); }, function (newValue) {
        setValue(newValue);
    }, [context, memoFunc]);
    return value;
}
function useSingleSharedValue(key) {
    var context = useSharedValueStoreContext();
    var _a = useState(function () { return context.get(key); }), value = _a[0], setValue = _a[1];
    useEffect(updateWithStoreValue, [context]);
    useLatestChanges(function () { return context.changes(key); }, function (newValue) {
        setValue(newValue);
    }, [context]);
    var setStoreValue = useCallback(function (value) { return context.set(key, value); }, [context]);
    function updateWithStoreValue() {
        var storeValue = context.get(key);
        if (storeValue !== value) {
            setValue(storeValue);
        }
    }
    return [value, setStoreValue];
}
function isValueFunction(key) {
    return typeof key === 'function';
}
function isCollectionKey(key) {
    return key['collection'] === true;
}
function isResolvedCollectionKey(key) {
    return key.resolver !== undefined;
}
function useModifySingleSharedValue(key) {
    var context = useSharedValueStoreContext();
    return useCallback(function (value) { return context.set(key, value); }, [context]);
}
function useReadFunction(func) {
    var context = useSharedValueStoreContext();
    return useCallback(function () { return context.get(func); }, [context, func]);
}
function useReadSingleSharedValue(key) {
    var context = useSharedValueStoreContext();
    return useCallback(function () { return context.get(key); }, [context, key]);
}
export function useLatestChanges(changes, onChanges, volatileArgs) {
    if (volatileArgs === void 0) { volatileArgs = []; }
    useEffect(function () {
        var sub = changes().subscribe(onChanges);
        return function () { return sub.unsubscribe(); };
    }, volatileArgs);
}
//# sourceMappingURL=use-shared-value.js.map